import React from 'react';
import { View, Text, StyleSheet, ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors, Fonts, Radii, Spacing, Typography } from '../constants';
import { Button } from './Button';

interface EmptyStateProps {
  icon?: keyof typeof Ionicons.glyphMap;
  title: string;
  /** One line on why it matters */
  subtitle?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** `inline`: linen hairline panel inside a section. `full`: centred on an empty screen. */
  variant?: 'inline' | 'full';
  style?: ViewStyle;
}

/**
 * Empty-state block (design v2): linen hairline panel, tinted icon square with
 * an accent-strong glyph, plain title, one purpose line, and a white (inline)
 * or primary (full) CTA. Calm statements, no exclamation marks.
 */
export const EmptyState: React.FC<EmptyStateProps> = ({
  icon = 'shirt-outline',
  title,
  subtitle,
  actionLabel,
  onAction,
  variant = 'inline',
  style,
}) => {
  const full = variant === 'full';
  return (
    <View style={[styles.container, full ? styles.containerFull : styles.containerInline, style]}>
      <View style={[styles.iconCircle, full && styles.iconCircleFull]}>
        <Ionicons name={icon} size={full ? 32 : 26} color={Colors.accentStrong} />
      </View>
      <Text style={[full ? Typography.section : styles.titleInline, styles.title, full && styles.titleFull]}>
        {title}
      </Text>
      {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
      {actionLabel && onAction ? (
        <Button
          title={actionLabel}
          icon="add"
          variant={full ? 'primary' : 'white'}
          onPress={onAction}
          style={styles.action}
        />
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    gap: Spacing.sm,
  },
  containerInline: {
    paddingVertical: 28,
    paddingHorizontal: Spacing.lg,
    backgroundColor: Colors.background,
    borderWidth: 1,
    borderColor: Colors.borderLight,
    borderRadius: Radii.card,
  },
  containerFull: {
    paddingVertical: Spacing['4xl'],
    paddingHorizontal: Spacing.xl,
  },
  iconCircle: {
    width: 56,
    height: 56,
    borderRadius: Radii.card,
    backgroundColor: Colors.primaryLight,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: Spacing.xs,
  },
  iconCircleFull: {
    width: 72,
    height: 72,
  },
  titleInline: {
    fontFamily: Fonts.semiBold,
    fontSize: 16,
    lineHeight: 21,
  },
  title: {
    color: Colors.text,
    textAlign: 'center',
  },
  titleFull: {
    color: Colors.textDark,
  },
  subtitle: {
    fontFamily: Fonts.regular,
    fontSize: 14,
    lineHeight: 21,
    color: Colors.textMedium,
    textAlign: 'center',
    maxWidth: 320,
  },
  action: {
    marginTop: Spacing.sm,
  },
});
